import type { SingleView } from '../../core/view-manager/single-view.js';
import { aggregate, type EvalContext, evaluate } from './evaluator.js';
import { FORMULA_PROPERTY_TYPE, type FormulaPropertyData } from './types.js';

/** Coerce a cell's jsonValue (number | numeric string | bool | null) to a number. */
export function toNumber(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string') {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/**
 * Evaluate a Formula column for a single row against the live view.
 *
 * Both the cell renderer (per-row display) and the column-stats footer
 * (totals) call this so they stay in lock-step. References to other Formula
 * columns recurse (e.g. Остаток = План − Оплачено where План is itself a
 * formula); a `${rowId}:${propId}` cycle guard prevents infinite loops.
 *
 * Returns `null` for an empty expression, `NaN` for a malformed one (the
 * renderer shows "Error"; aggregates/totals skip non-finite values).
 */
export function computeFormulaValue(
  view: SingleView,
  rowId: string,
  propId: string,
  data: FormulaPropertyData,
  stack: Set<string> = new Set()
): number | null {
  const rowExpression = view.cellGetOrCreate(rowId, propId).value$.value;
  const expression =
    typeof rowExpression === 'string'
      ? rowExpression.trim()
      : data.expression?.trim();
  if (!expression) return null;

  const guardKey = `${rowId}:${propId}`;
  if (stack.has(guardKey)) return 0; // cycle — bail out
  stack.add(guardKey);

  const resolveRow = (colId: string, row: string): number => {
    const target = view.propertyGetOrCreate(colId);
    if (target.type$.value === FORMULA_PROPERTY_TYPE) {
      const nested = computeFormulaValue(
        view,
        row,
        colId,
        target.data$.value as FormulaPropertyData,
        stack
      );
      return nested == null || Number.isNaN(nested) ? 0 : nested;
    }
    return toNumber(view.cellGetOrCreate(row, colId).jsonValue$.value);
  };

  const ctx: EvalContext = {
    ref: colId => resolveRow(colId, rowId),
    agg: (fn, colId) => {
      const values: number[] = [];
      for (const row of view.rows$.value) {
        values.push(resolveRow(colId, row.rowId));
      }
      return aggregate(fn, values);
    },
  };

  try {
    return evaluate(expression, ctx);
  } catch {
    return NaN;
  } finally {
    stack.delete(guardKey);
  }
}
